import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, FormSection, getFormValues, reduxForm } from 'redux-form';
import { FormattedMessage } from 'react-intl';
import mixpanel from 'mixpanel-browser';

import { formSettings } from './../settings';
import messages from '../../../../../../text/messages';
import { TextField } from './../../../../../../components/fields';
import RadioField from './../../../../../../components/genFields/radio';
import WizardButtons from '../../../../../../components/wizard/navigator/buttons';
import { DocumentInfo } from '../../../../../../utilities/documentTypes';
import { getAdmin, setProgressionProperties } from '../../../../../../reducers/onboarding';
import { updateUserProfile } from '../../../../../../reducers/identity';
import User from '../../../../../../models/user';
import { isTrue, maskSSN } from '../../../../../../utilities/string';
import Document from '../../../../../../components/document';
import { PERMISSIONS } from '../../../../../../components/document/types';
import FieldGenerator from '../../../../../../components/fields/generator';
import Signature from '../../../../../../components/signature';
import { Signatures } from '../../../../../../utilities/signatureTypes';
import { getSignatures, sign } from '../../../../../../reducers/signatures';
import { required } from '../../../../../../utilities/validation';
import options from '../../../../../../text/selectOptions';
const { newStarterInfoQuestions } = options;

class NewStarterInfo extends React.Component {
    constructor(props) {
        super(props);

        this.submit = this.submit.bind(this);
        this.getEmployeeStatementOptions = this.getEmployeeStatementOptions.bind(this);
        this.renderRadio = this.renderRadio.bind(this);
    }

    componentDidMount() {
        mixpanel.track('WorkerOnboarding', { _data: { branch: 'employee', step: 'W4Step' } });

        const { profile, admin, client, getAdmin, setProgressionProperties, getSignatures, signatureList, role, job } = this.props;
        if (!admin) {
            getAdmin(client.admin_id);
        }

        let personalInformation = {
            firstName: profile.first_name,
            middleName: profile.middle_name,
            lastName: profile.last_name,
            email: profile.email,
            phoneNumber: profile.phone,
            dateOfBirth: profile.birthday_date,
            socialSecurityNumber: maskSSN(profile.ssn_bg_check),
            emergency_name: profile.emergency_name,
            emergency_email: profile.emergency_email,
            emergency_phone: profile.emergency_phone,
            emergency_relationship: profile.emergency_relationship,
            w4_fields: profile.w4_fields,
            i9_fields: profile.i9_fields
        };

        let dateOfBirth = { day: null, month: null, year: null };
        if (personalInformation.dateOfBirth) {
            let date = new Date(personalInformation.dateOfBirth + 'T00:00:00');
            dateOfBirth = {
                date: personalInformation.dateOfBirth,
                day: isNaN(date.getDate()) ? null : date.getDate(),
                month: isNaN(date.getMonth()) ? null : date.getMonth() + 1,
                year: isNaN(date.getFullYear()) ? null : date.getFullYear()
            };
        }

        setProgressionProperties({ personalInformation: { ...personalInformation, dateOfBirth } });
        if (!signatureList) {
            getSignatures(role, result => {
                setProgressionProperties({
                    newCheckListSigned: result.find(s => s.name === `${Signatures.types.NEW_STARTER_CHECKLIST.name}:${job.id}`) !== undefined
                });
            });
        }
    }

    submit(values, onComplete) {
        const { profile, updateUserProfile, personalInformation, setProgressionProperties, sign, role, job } = this.props;
        const { w4_fields } = values;

        updateUserProfile(new User({ ...profile, w4_fields }), () => {
            setProgressionProperties({ personalInformation: { ...personalInformation, w4_fields } });
            sign(role, `${Signatures.types.NEW_STARTER_CHECKLIST.name}:${job.id}`, () => {
                setProgressionProperties({ newCheckListSigned: true });
                onComplete();
            });
        });
    }

    renderRadio(field, answer) {
        const { formValues } = this.props;
        const value = ((formValues || {}).w4_fields || {})[field] || { value: '' };
        return <i className="material-icons">{answer.toString() === value.toString() ? 'radio_button_checked' : 'radio_button_unchecked'}</i>;
    }

    getEmployeeStatementOptions() {
        const field = 'employee_statement';
        return [
            {
                value: 'first',
                label: (
                    <div className="radio-field-horizontal-radios topOffset15">
                        {this.renderRadio(field, 'first')}
                        <span className="text-left">
                            <FormattedMessage {...messages.employeeStatementFirst} />
                        </span>
                    </div>
                )
            },
            {
                value: 'another',
                label: (
                    <div className="radio-field-horizontal-radios topOffset15">
                        {this.renderRadio(field, 'another')}
                        <span className="text-left">
                            <FormattedMessage {...messages.employeeStatementAnother} />
                        </span>
                    </div>
                )
            },
            {
                value: 'other',
                label: (
                    <div className="radio-field-horizontal-radios topOffset15">
                        {this.renderRadio(field, 'other')}
                        <span className="text-left">
                            <FormattedMessage {...messages.employeeStatementOther} />
                        </span>
                    </div>
                )
            }
        ];
    }

    render() {
        const { handleSubmit, onSubmit, back, submitting, admin, formValues, job } = this.props;
        if (!admin) {
            return null;
        }

        const { studentLoanPlan, studiesCompletion, creditCardRepaying, postgraduateLoan, postgraduateCompletion } = (formValues || {}).w4_fields || {};
        return (
            <form onSubmit={handleSubmit(values => this.submit(values, onSubmit))}>
                <div className="row section">
                    <div className="col-xs-12">
                        <FormattedMessage
                            id={'newStarter.intro'}
                            defaultMessage={
                                'As a new employee GreenLight needs to provide the following information to HMRC and to determine your correct tax code.'
                            }
                        />
                    </div>
                </div>

                <FormSection name="w4_fields">
                    <div className="row">
                        <div className="col-xs-12">
                            <span className="bold">
                                <FormattedMessage {...messages.employeeStatement} />
                            </span>
                        </div>
                    </div>
                    <div className="row section topOffset15">
                        <div className="col-xs-12">
                            <div className="form-generator">
                                <Field
                                    name="employee_statement"
                                    component={RadioField}
                                    options={this.getEmployeeStatementOptions()}
                                    placeholder={messages.employeeStatementLabel}
                                    validate={required}
                                    horizontal
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row section">
                        <div className="col-xs-12">
                            <span className="bold">
                                <FormattedMessage {...messages.previousEmployment} />
                            </span>
                        </div>
                        <div className="col-xs-12 section topOffset15">
                            <FormattedMessage
                                id={'newStarter.P45'}
                                defaultMessage={'If you have a Tax code and a P45 from your most recent employment, please provide them below.'}
                            />
                        </div>
                        <div className="col-xs-12">
                            <div className="row">
                                <div className="col-md-3 col-xs-12">
                                    <Field name="tax_code" component={TextField} placeholder={messages.taxCode} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 col-xs-12">
                                    <Document
                                        {...DocumentInfo.types.CONTRACTOR_P45}
                                        upload={{ limit: 1, permissions: PERMISSIONS.CLIENT_UPLOAD }}
                                        view={{ showHeader: false, component: 'icon-download', remove: true }}
                                        remove={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row section">
                        <div className="col-xs-12">
                            <span className="bold">
                                <FormattedMessage {...messages.studentLoan} />
                            </span>
                        </div>
                        <div className="col-xs-12 topOffset15">
                            <FormattedMessage
                                id={'newStarter.studLoan'}
                                defaultMessage={'If you would like some more information about repaying your student loan, take a look at'}
                            />
                            <div>
                                <a href="https://www.gov.uk/repaying-your-student-loan" rel="noopener noreferrer" target="_blank">
                                    www.gov.uk/funding-for-postgraduate-study
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-7 col-xs-12">
                            <FieldGenerator
                                definition={newStarterInfoQuestions.studentLoanPlan}
                                allQuestions={[]}
                                formName={formSettings.form}
                                formSectionName={'w4_fields'}
                                validate={required}
                                skipOnChange={true}
                            />

                            {isTrue((studentLoanPlan || {}).value) && (
                                <FieldGenerator
                                    definition={newStarterInfoQuestions.studiesCompletion}
                                    allQuestions={[]}
                                    formName={formSettings.form}
                                    formSectionName={'w4_fields'}
                                    validate={required}
                                    skipOnChange={true}
                                />
                            )}
                            {isTrue((studentLoanPlan || {}).value) &&
                                isTrue((studiesCompletion || {}).value) && (
                                    <FieldGenerator
                                        definition={newStarterInfoQuestions.creditCardRepaying}
                                        allQuestions={[]}
                                        formName={formSettings.form}
                                        formSectionName={'w4_fields'}
                                        validate={required}
                                        skipOnChange={true}
                                    />
                                )}
                            {isTrue((studentLoanPlan || {}).value) &&
                                isTrue((studiesCompletion || {}).value) &&
                                (creditCardRepaying || {}).value === 'false' && (
                                    <FieldGenerator
                                        definition={newStarterInfoQuestions.studentLoanType}
                                        allQuestions={[]}
                                        validate={required}
                                        formName={formSettings.form}
                                        formSectionName={'w4_fields'}
                                    />
                                )}
                        </div>
                    </div>

                    <div className="row section">
                        <div className="col-xs-12">
                            <span className="bold">
                                <FormattedMessage {...messages.postgraduateLoan} />
                            </span>
                        </div>
                        <div className="col-xs-12 topOffset15">
                            <FormattedMessage
                                id={'newStarter.PostLoan'}
                                defaultMessage={'If you would like some more information about repaying your Postgraduate Loan, take a look at '}
                            />
                            <div>
                                <a href="https://www.gov.uk/funding-for-postgraduate-study" rel="noopener noreferrer" target="_blank">
                                    www.gov.uk/funding-for-postgraduate-study
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-7 col-xs-12 form-generator">
                            <FieldGenerator
                                definition={newStarterInfoQuestions.postgraduateLoan}
                                allQuestions={[]}
                                formName={formSettings.form}
                                formSectionName={'w4_fields'}
                                validate={required}
                                skipOnChange={true}
                            />
                            {isTrue((postgraduateLoan || {}).value) && (
                                <FieldGenerator
                                    definition={newStarterInfoQuestions.postgraduateCompletion}
                                    allQuestions={[]}
                                    formName={formSettings.form}
                                    formSectionName={'w4_fields'}
                                    validate={required}
                                    skipOnChange={true}
                                />
                            )}
                            {isTrue((postgraduateLoan || {}).value) &&
                                isTrue((postgraduateCompletion || {}).value) && (
                                    <FieldGenerator
                                        definition={newStarterInfoQuestions.postgraduateCreditCardRepaying}
                                        allQuestions={[]}
                                        formName={formSettings.form}
                                        formSectionName={'w4_fields'}
                                        validate={required}
                                        skipOnChange={true}
                                    />
                                )}
                        </div>
                    </div>
                </FormSection>
                <div className="row section">
                    <div className="col-xs-12">
                        <span className="bold">
                            <FormattedMessage {...messages.declaration} />
                        </span>
                    </div>
                    <div className="col-xs-12 topOffset15">
                        <span>
                            <FormattedMessage {...messages.iConfirm} />
                        </span>
                    </div>
                    <div className="col-xs-12">
                        <Signature name={`${Signatures.types.NEW_STARTER_CHECKLIST.name}:${job.id}`} />
                    </div>
                </div>

                <WizardButtons disabled={submitting} back={{ action: back }} submit={{ message: <FormattedMessage {...messages.signAndContinue} /> }} />
            </form>
        );
    }
}

NewStarterInfo = reduxForm(formSettings)(NewStarterInfo);

const mapStateToProps = state => {
    let personalInformation = state.onboarding.progression.personalInformation;
    const formValues = getFormValues(formSettings.form)(state);

    return {
        role: state.identity.role,
        profile: state.identity.profile,
        admin: state.onboarding.admin,
        client: state.onboarding.client,
        job: state.jobs.list[0],
        personalInformation,
        initialValues: personalInformation ? { w4_fields: personalInformation.w4_fields } : undefined,
        formValues,
        signatureList: state.signatures.list,
        isSigned: state.onboarding.progression.newCheckListSigned
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getAdmin,
            updateUserProfile,
            setProgressionProperties,
            sign,
            getSignatures
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(NewStarterInfo);



// WEBPACK FOOTER //
// ./src/pages/onboarding/tenants/contractor/steps/employee/newStarterInfo.jsx